export class PromptBuilders {
  static buildExpandDescriptionPrompt(initialDescription: string): string {
    return `Você é um consultor sênior especializado em gestão de projetos de software, análise de negócios e definição de requisitos. Sua missão é transformar uma descrição inicial básica em uma especificação detalhada, profunda e profissional de projeto de software.

DESCRIÇÃO INICIAL:
${initialDescription}

INSTRUÇÕES DETALHADAS:
1. ANÁLISE PROFUNDA: Examine minuciosamente a descrição inicial, identificando lacunas, oportunidades e potencial de expansão
2. CONTEXTUALIZAÇÃO AMPLA: Situe o projeto no contexto de mercado, tendências tecnológicas e necessidades empresariais atuais
3. PROBLEMA & SOLUÇÃO: Articule claramente o problema de negócio, suas implicações e como a solução proposta irá solucioná-lo de forma diferenciada
4. OBJETIVOS ESTRATÉGICOS: Desenvolva objetivos de negócio mensuráveis e alinhados com resultados tangíveis
5. ANÁLISE DE STAKEHOLDERS: Identifique todos os possíveis usuários, decisores e afetados pelo projeto
6. FUNCIONALIDADES CORE: Detalhe as funcionalidades essenciais com suas respectivas justificativas de valor
7. DIFERENCIAIS COMPETITIVOS: Identifique elementos únicos que destacam esta solução no mercado
8. CONSIDERAÇÕES TÉCNICAS: Mencione arquiteturas, integrações e requisitos técnicos relevantes
9. ESTIMATIVA DE IMPACTO: Avalie o impacto esperado nos processos, usuários e negócio

CRITÉRIOS DE QUALIDADE PARA A RESPOSTA:
- A descrição expandida deve ter pelo menos 300-500 palavras
- Usar linguagem profissional e técnica apropriada
- Incluir justificativas para cada escolha e sugestão
- Demonstrar profundidade de análise e visão estratégica
- Considerar aspectos de usabilidade, escalabilidade e manutenibilidade

FORMATO DE RESPOSTA:
Responda APENAS em JSON no seguinte formato (sem markdown ou texto adicional):
{
  "expandedDescription": "DESCRIÇÃO EXPANDIDA DETALHADA E PROFISSIONAL DO PROJETO - Esta deve ser uma análise profunda e abrangente de pelo menos 300-500 palavras que inclua: contexto de negócio, problema detalhado, solução proposta, justificativas estratégicas, impacto esperado, diferenciais competitivos e visão de longo prazo do projeto...",
  "suggestedName": "Nome sugerido profissional e impactante para o projeto",
  "estimatedComplexity": "low|medium|high",
  "keyFeatures": ["Funcionalidade detalhada 1 com justificativa", "Funcionalidade detalhada 2 com justificativa", "Funcionalidade detalhada 3 com justificativa", "Funcionalidade detalhada 4 com justificativa", "Funcionalidade detalhada 5 com justificativa"],
  "targetUsers": ["Tipo de usuário detalhado 1", "Tipo de usuário detalhado 2", "Tipo de usuário detalhado 3"],
  "technicalConsiderations": ["Consideração técnica detalhada 1", "Consideração técnica detalhada 2", "Consideração técnica detalhada 3", "Consideração técnica detalhada 4"],
  "businessValue": "Análise detalhada do valor de negócio e ROI esperado",
  "marketContext": "Contexto de mercado e posicionamento competitivo",
  "riskFactors": ["Fator de risco 1 e mitigação", "Fator de risco 2 e mitigação"],
  "successMetrics": ["Métrica de sucesso 1", "Métrica de sucesso 2", "Métrica de sucesso 3"]
}

IMPORTANTE: A descrição expandida deve ser substancialmente mais detalhada que a inicial, demonstrando análise profunda e visão estratégica completa do projeto.`;
  }

  static buildObjectivesPrompt(projectDescription: string): string {
    return `Baseado na seguinte descrição de projeto, gere uma lista de objetivos SMART (Específicos, Mensuráveis, Atingíveis, Relevantes, Temporais):

DESCRIÇÃO DO PROJETO:
${projectDescription}

Responda APENAS com uma lista JSON de objetivos (sem markdown ou texto adicional):
["objetivo1", "objetivo2", "objetivo3"]`;
  }

  static buildTechnicalSolutionPrompt(
    technicalDescription: string,
    objectives: string[],
    projectContext?: {
      projectDescription: string;
      projectName: string;
    }
  ): string {
    return `Você é um CTO e arquiteto de software sênior com mais de 15 anos de experiência em design de sistemas complexos, escaláveis e de alta disponibilidade. Sua missão é criar uma especificação técnica completa, profunda e detalhada que sirva como blueprint definitivo para o desenvolvimento do projeto.

${
  projectContext
    ? `CONTEXTO COMPLETO DO PROJETO:
Nome: ${projectContext.projectName}
Descrição: ${projectContext.projectDescription}

OBJETIVOS DE NEGÓCIO:
${objectives.map((obj, i) => `${i + 1}. ${obj}`).join("\n")}
`
    : ""
}

DESCRIÇÃO TÉCNICA INICIAL:
${technicalDescription}

INSTRUÇÕES DETALHADAS PARA ANÁLISE TÉCNICA:
1. ARQUITETURA SISTÊMICA: Proponha uma arquitetura completa (microserviços, monólito modular, serverless, híbrida) com justificativas detalhadas baseadas em escalabilidade, manutenibilidade e complexidade
2. STACK TECNOLÓGICO: Selecione tecnologias específicas considerando: performance, comunidade, curva de aprendizado, longevidade, compatibilidade e custo total de propriedade (TCO)
3. PADRÕES ARQUITETURAIS: Defina padrões de design, arquitetural e organizacionais (DDD, CQRS, Event Sourcing, Clean Architecture, etc.)
4. REQUISITOS NÃO FUNCIONAIS: Especifique métricas concretas para performance, segurança, disponibilidade, escalabilidade, usabilidade e manutenibilidade
5. ESTRATÉGIA DE DADOS: Defina modelo de dados, estratégias de persistência, cache, backup e conformidade com LGPD/GDPR
6. INTEGRAÇÃO & APIs: Detalhe estratégias de integração, versionamento de APIs, contratos e protocolos de comunicação
7. SEGURANÇA EM PROFUNDIDADE: Implemente camadas de segurança (autenticação, autorização, criptografia, auditoria, proteção contra ameaças)
8. OBSERVABILIDADE: Defina estratégias de logging, monitoramento, métricas, tracing distribuído e alerting
9. DEPLOY & DEVOPS: Especifique CI/CD, estratégias de deployment, infraestrutura como código, containerização
10. GOVERNANÇA TÉCNICA: Estabeleça padrões de código, revisão, documentação e evolução da arquitetura

CRITÉRIOS DE QUALIDADE TÉCNICA:
- Solução técnica expandida deve ter pelo menos 400-600 palavras
- Justificativas técnicas sólidas baseadas em experiência e melhores práticas
- Consideração de trade-offs e alternativas
- Foco em manutenibilidade, escalabilidade e sustentabilidade de longo prazo
- Alinhamento com objetivos de negócio e restrições orçamentárias

FORMATO DE RESPOSTA:
Responda APENAS em JSON no seguinte formato (sem markdown ou texto adicional):
{
  "expandedDescription": "ESPECIFICAÇÃO TÉCNICA COMPLETA E DETALHADA - Uma análise técnica profunda de 400-600 palavras que inclua: arquitetura proposta, justificativas técnicas, considerações de escalabilidade, estratégias de integração, requisitos de infraestrutura, plano de evolução tecnológica e alinhamento com objetivos de negócio...",
  "suggestedArchitecture": "Descrição arquitetural completa com diagramas conceituais, fluxo de dados, comunicação entre componentes e estratégias de deployment",
  "recommendedTechnologies": {
    "frontend": [
      {
        "name": "React",
        "version": "18.x",
        "justification": "Biblioteca moderna e madura para interfaces reativas com ecossistema robusto, TypeScript nativo, performance otimizada através de React 18 features (Concurrent Features, Suspense), vasta comunidade e suporte corporativo do Meta. Oferece excelente developer experience e facilita manutenção de código complexo.",
        "alternatives": ["Vue.js 3.x com Composition API", "Angular 17+ com Standalone Components", "Svelte/SvelteKit para performance máxima"]
      }
    ],
    "backend": [
      {
        "name": "Node.js",
        "version": "20.x LTS",
        "justification": "Runtime JavaScript de alta performance baseado em V8, ideal para aplicações I/O intensivas. LTS garante estabilidade e suporte de longo prazo. Vasto ecossistema NPM, facilita compartilhamento de código entre frontend/backend, excelente para APIs REST/GraphQL e microserviços.",
        "alternatives": ["Python 3.11+ com FastAPI", "Java 21 LTS com Spring Boot 3", "Go 1.21+ para performance extrema", ".NET 8 para ambientes Microsoft"]
      }
    ],
    "database": [
      {
        "name": "PostgreSQL", 
        "version": "15.x",
        "justification": "SGBD relacional mais avançado do mercado, com excelente suporte para JSON (JSONB), extensibilidade através de extensões, ACID compliance, suporte robusto para transações complexas, particionamento nativo, replicação e alta disponibilidade. Ideal para aplicações que precisam de consistência e flexibilidade.",
        "alternatives": ["MongoDB 7.x para dados não-relacionais", "MySQL 8.x para simplicidade", "Redis para cache e sessões", "ClickHouse para analytics"]
      }
    ],
    "infrastructure": [
      {
        "name": "AWS",
        "version": "latest",
        "justification": "Líder de mercado em cloud computing com mais de 200 serviços gerenciados, excelente para escalabilidade automática, alta disponibilidade multi-região, compliance com regulamentações internacionais, amplo ecossistema de ferramentas, pricing competitivo para diferentes workloads e suporte técnico robusto.",
        "alternatives": ["Azure para ambientes Microsoft-first", "Google Cloud para ML/AI workloads", "Digital Ocean para simplicity-first", "Vercel/Netlify para aplicações frontend"]
      }
    ],
    "devops": [
      {
        "name": "Docker",
        "version": "24.x",
        "justification": "Containerização padrão da indústria, garante consistência entre ambientes de desenvolvimento, teste e produção, facilita deploy e scaling, integração nativa com Kubernetes e cloud providers.",
        "alternatives": ["Podman como alternativa rootless", "containerd para runtime minimalista"]
      }
    ]
  },
  "designPatterns": ["Clean Architecture para separação de responsabilidades", "Repository Pattern para abstração de dados", "CQRS para separação de leitura/escrita", "Event-Driven Architecture para desacoplamento", "Circuit Breaker para resilência"],
  "nonFunctionalRequirements": {
    "performance": "Tempo de resposta < 200ms para 95% das requisições, throughput de 1000+ RPS, cache multi-camada com Redis, lazy loading e code splitting no frontend, índices otimizados no banco de dados",
    "security": "Autenticação JWT/OAuth 2.0, autorização baseada em roles (RBAC), criptografia TLS 1.3, sanitização de inputs, rate limiting, OWASP Top 10 compliance, auditoria completa de ações, conformidade com LGPD/GDPR",
    "scalability": "Horizontal scaling com load balancers, auto-scaling baseado em métricas, arquitetura stateless, database sharding/partitioning, CDN para assets estáticos, cache distribuído",
    "availability": "SLA de 99.9% uptime, deploy blue-green com rollback automático, health checks e circuit breakers, backup automatizado com RTO < 4h e RPO < 1h, monitoramento 24/7",
    "maintainability": "Code coverage > 80%, documentação técnica automatizada, padrões de código enforçados por ESLint/Prettier, conventional commits, dependency updates automatizados"
  },
  "technicalComplexity": "medium|high",
  "technicalRisks": [
    "Vendor lock-in com cloud provider - Mitigação: uso de abstrações e padrões cloud-agnostic",
    "Escalabilidade de banco de dados - Mitigação: implementar sharding e read replicas desde o início",
    "Complexidade de microserviços - Mitigação: iniciar com monólito modular e evoluir gradualmente",
    "Curva de aprendizado da equipe - Mitigação: treinamentos, pair programming e mentoria técnica",
    "Dependências de terceiros - Mitigação: versionamento rigoroso, testes de compatibilidade e planos de contingência"
  ],
  "implementationStrategy": "Estratégia detalhada de implementação em fases com MVPs incrementais, cronograma técnico, definição de marcos de entrega, estratégia de testes (unitários, integração, e2e), plano de migração de dados se aplicável, e critérios de aceitação técnica",
  "developmentPhases": [
    "Fase 1: Setup de infraestrutura e arquitetura base (4-6 semanas)",
    "Fase 2: Implementação de funcionalidades core e MVP (8-10 semanas)", 
    "Fase 3: Integração, testes e otimizações (4-6 semanas)",
    "Fase 4: Deploy, monitoramento e ajustes finais (2-3 semanas)"
  ],
  "teamRequirements": "Especificação detalhada do time necessário incluindo papéis técnicos, seniority requerida, skills específicos e estrutura organizacional recomendada"
}

IMPORTANTE: A solução técnica deve demonstrar profundidade de conhecimento arquitetural e ser aplicável imediatamente como guia de desenvolvimento, considerando todas as nuances técnicas e de negócio do projeto.`;
  }

  static buildMilestonesPrompt(
    projectDescription: string,
    objectives: string[],
    technicalSolution?: string,
    timeline?: { months: number; startDate: string; endDate: string }
  ): string {
    return `Você é um gerente de projeto especializado em planejamento e criação de marcos (milestones). Baseado nas informações do projeto, crie marcos realistas e bem estruturados.

DESCRIÇÃO DO PROJETO:
${projectDescription}

OBJETIVOS:
${objectives.map((obj, i) => `${i + 1}. ${obj}`).join("\n")}

${
  technicalSolution
    ? `SOLUÇÃO TÉCNICA:
${technicalSolution}`
    : ""
}

${
  timeline
    ? `CRONOGRAMA:
Duração: ${timeline.months} meses
Início: ${timeline.startDate}
Fim: ${timeline.endDate}`
    : ""
}

INSTRUÇÕES:
1. Crie marcos (milestones) realistas para o projeto
2. Cada marco deve ter título, descrição, prazo e deliverables
3. Considere dependências entre marcos
4. Inclua marcos de planejamento, desenvolvimento e entrega
5. Defina critérios de aceitação para cada marco
6. Estime percentual de conclusão do projeto em cada marco

FORMATO DE RESPOSTA:
Responda APENAS em JSON no seguinte formato (sem markdown ou texto adicional):
{
  "milestones": [
    {
      "title": "Nome do Marco",
      "description": "Descrição detalhada do marco",
      "dueDate": "YYYY-MM-DD",
      "deliverables": ["deliverable1", "deliverable2"],
      "acceptanceCriteria": ["critério1", "critério2"],
      "projectCompletion": 25,
      "priority": "high|medium|low",
      "dependencies": ["Marco anterior"]
    }
  ]
}

Certifique-se de que os marcos sejam sequenciais, realistas e cubram todo o ciclo de vida do projeto.`;
  }

  static buildUserStoriesPrompt(
    projectDescription: string,
    objectives: string[],
    technicalSolution?: string,
    milestones?: any[]
  ): string {
    return `Você é um Product Owner experiente especializado em criação de user stories e épicos. Baseado nas informações do projeto, crie user stories detalhadas seguindo as melhores práticas ágeis.

DESCRIÇÃO DO PROJETO:
${projectDescription}

OBJETIVOS:
${objectives.map((obj, i) => `${i + 1}. ${obj}`).join("\n")}

${
  technicalSolution
    ? `SOLUÇÃO TÉCNICA:
${technicalSolution}`
    : ""
}

${
  milestones && milestones.length > 0
    ? `MARCOS DO PROJETO:
${milestones.map((m, i) => `${i + 1}. ${m.title} - ${m.description}`).join("\n")}`
    : ""
}

INSTRUÇÕES:
1. Crie épicos que agrupem funcionalidades relacionadas
2. Para cada épico, crie user stories detalhadas
3. IMPORTANTE: Forneça campos SEPARADOS para role, functionality, e benefit:
   - role: Identifique o tipo específico de usuário (ex: "administrador do sistema", "cliente final", "vendedor")
   - functionality: Descreva a ação específica que quer realizar (ex: "visualizar relatórios de vendas", "cadastrar novos produtos")
   - benefit: Explique o valor/benefício (ex: "tomar decisões baseadas em dados", "manter o catálogo atualizado")
4. O campo description deve conter contexto adicional, não repetir o formato "Como...eu quero...para que"
5. Inclua critérios de aceitação granulares e testáveis
6. Defina prioridade e pontos de story realistas
7. Considere diferentes personas do sistema
8. Inclua stories técnicas quando necessário

FORMATO DE RESPOSTA:
IMPORTANTE: Responda APENAS com JSON válido. NÃO inclua:
- Comentários JavaScript (//)
- Texto explicativo
- Markdown
- Blocos de código
- Texto adicional antes ou depois do JSON

ESTRUTURA JSON EXIGIDA:
- Objeto principal com array "epics"
- Cada épico deve ter: id, title, description, priority, stories
- Cada story deve ter: id, title, description, role, functionality, benefit, acceptanceCriteria, priority, storyPoints, estimatedHours, epic, type

REGRAS CRÍTICAS:
1. JSON deve ser válido e completo
2. NUNCA inclua comentários (// ou /* */)  
3. NUNCA use "..." ou reticências
4. Inclua TODAS as stories completas
5. Use apenas valores literais, não placeholders
6. Inicie diretamente com {
7. Termine com }

Certifique-se de cobrir todas as funcionalidades principais e criar stories granulares e testáveis.`;
  }

  static buildSchemaPrompt(request: any): string {
    return `Você é um arquiteto de dados especializado em modelagem de banco de dados e design de APIs. Baseado nas informações do projeto, crie um schema técnico completo incluindo modelos de dados e endpoints de API.

DESCRIÇÃO DO PROJETO:
${request.projectDescription}

OBJETIVOS:
${request.objectives.map((obj: string, i: number) => `${i + 1}. ${obj}`).join("\n")}

${
  request.technicalSolution
    ? `SOLUÇÃO TÉCNICA:
${request.technicalSolution}`
    : ""
}

${
  request.stories
    ? `USER STORIES:
${JSON.stringify(request.stories, null, 2)}`
    : ""
}

CONFIGURAÇÕES:
- Banco de dados: ${request.databasePreference || "PostgreSQL"}
- Estilo de API: ${request.apiStyle || "REST"}
- Autenticação: ${request.authRequired ? "Obrigatória" : "Opcional"}
- Documentação: ${request.includeDocumentation ? "Incluir" : "Não incluir"}

INSTRUÇÕES:
1. Crie modelos de dados (tabelas) necessários
2. Defina campos, tipos, validações e relacionamentos
3. Crie endpoints de API para operações CRUD
4. Inclua parâmetros, responses e códigos de status
5. Agrupe endpoints logicamente
6. Inclua documentação se solicitado
7. Configure aspectos técnicos da API

FORMATO DE RESPOSTA:
Responda APENAS em JSON no seguinte formato (sem markdown ou texto adicional):
{
  "dataModels": [
    {
      "id": "model-1",
      "name": "User",
      "description": "Modelo de usuário do sistema",
      "tableName": "users",
      "fields": [
        {
          "id": "field-1",
          "name": "id",
          "type": "uuid",
          "description": "Identificador único",
          "validation": {
            "required": true,
            "unique": true,
            "indexed": true
          },
          "example": "550e8400-e29b-41d4-a716-446655440000"
        }
      ],
      "relationships": [],
      "timestamps": true,
      "softDelete": false
    }
  ],
  "apiEndpoints": [
    {
      "id": "endpoint-1",
      "path": "/api/users",
      "method": "GET",
      "summary": "Listar usuários",
      "description": "Retorna lista paginada de usuários",
      "parameters": [
        {
          "name": "page",
          "type": "query",
          "dataType": "integer",
          "required": false,
          "description": "Número da página",
          "example": "1"
        }
      ],
      "responses": [
        {
          "statusCode": 200,
          "description": "Lista de usuários retornada com sucesso",
          "schema": "UserListResponse",
          "example": {
            "data": [],
            "meta": {
              "total": 0,
              "page": 1,
              "limit": 20
            }
          }
        }
      ],
      "tags": ["Users"],
      "requiresAuth": true
    }
  ],
  "apiGroups": [
    {
      "name": "Users",
      "description": "Operações relacionadas a usuários",
      "endpoints": ["endpoint-1"]
    }
  ],
  "documentation": [
    {
      "title": "Introdução",
      "content": "Documentação da API do projeto"
    }
  ],
  "config": {
    "apiVersion": "v1",
    "baseUrl": "https://api.exemplo.com",
    "databaseType": "postgresql",
    "authenticationMethod": "jwt",
    "corsEnabled": true,
    "rateLimitEnabled": true,
    "loggingEnabled": true,
    "openApiInfo": {
      "title": "API do Projeto",
      "description": "Descrição da API",
      "version": "1.0.0"
    }
  }
}

Certifique-se de criar um schema coeso, bem estruturado e que atenda aos requisitos do projeto.`;
  }

  static buildDataModelsPrompt(
    projectDescription: string,
    objectives: string[],
    technicalContext: string,
    storiesContext: string,
    databasePreference: string
  ): string {
    return `Você é um arquiteto de dados especializado em modelagem de banco de dados. Baseado nas informações do projeto, crie modelos de dados detalhados e bem estruturados.

DESCRIÇÃO DO PROJETO:
${projectDescription}

OBJETIVOS:
${objectives.map((obj, i) => `${i + 1}. ${obj}`).join("\n")}

CONTEXTO TÉCNICO:
${technicalContext}

USER STORIES:
${storiesContext}

BANCO DE DADOS: ${databasePreference}

INSTRUÇÕES:
1. Analise os requisitos e identifique entidades principais
2. Crie modelos de dados com campos apropriados
3. Defina tipos de dados adequados para ${databasePreference}
4. Inclua validações e constraints
5. Considere relacionamentos entre modelos
6. Inclua timestamps e soft delete quando apropriado
7. Adicione exemplos realistas para cada campo

FORMATO DE RESPOSTA:
Responda APENAS com um array JSON de modelos (sem markdown ou texto adicional):
[
  {
    "id": "model-1",
    "name": "User",
    "description": "Modelo de usuário do sistema",
    "tableName": "users",
    "fields": [
      {
        "id": "field-1",
        "name": "id",
        "type": "uuid",
        "description": "Identificador único do usuário",
        "validation": {
          "required": true,
          "unique": true,
          "indexed": true
        },
        "example": "550e8400-e29b-41d4-a716-446655440000"
      }
    ],
    "relationships": [],
    "timestamps": true,
    "softDelete": false,
    "createdAt": "2024-01-01T00:00:00.000Z",
    "updatedAt": "2024-01-01T00:00:00.000Z"
  }
]

Certifique-se de criar modelos coesos que atendam aos requisitos funcionais do projeto.`;
  }

  static buildApiEndpointsPrompt(
    projectDescription: string,
    objectives: string[],
    dataModels: any[],
    apiStyle: string,
    authRequired: boolean,
    technicalSolution?: any,
    stories?: any,
    timeline?: any
  ): string {
    // Contexto enriquecido para melhor geração de endpoints
    return `Você é um arquiteto de APIs especializado. Analise COMPLETAMENTE o projeto e crie endpoints abrangentes baseados em TODAS as informações fornecidas.

📋 DESCRIÇÃO COMPLETA DO PROJETO:
${projectDescription}

🎯 OBJETIVOS COMPLETOS (${objectives.length} objetivos):
${objectives.map((obj, i) => `${i + 1}. ${obj}`).join('\n')}

${technicalSolution ? `
🔧 SOLUÇÃO TÉCNICA:
- Descrição: ${technicalSolution.description || 'N/A'}
- Stack Frontend: ${technicalSolution.recommendedStack?.frontend?.map((t: any) => t.name || t).join(', ') || 'N/A'}
- Stack Backend: ${technicalSolution.recommendedStack?.backend?.map((t: any) => t.name || t).join(', ') || 'N/A'}
- Arquitetura: ${technicalSolution.architecture?.pattern || 'N/A'}
- Complexidade: ${technicalSolution.complexity?.overall || 'medium'}
` : ''}

${stories && stories.stories?.length > 0 ? `
📚 USER STORIES (${stories.stories.length} histórias):
${stories.stories.slice(0, 10).map((story: any, i: number) => 
  `${i + 1}. [${story.priority}] ${story.title}
     - Como ${story.role}, eu quero ${story.functionality}, para que ${story.benefit}
     - Critérios: ${story.acceptanceCriteria?.slice(0, 2).map((c: any) => c.description).join('; ') || 'N/A'}`
).join('\n')}
${stories.stories.length > 10 ? `... e mais ${stories.stories.length - 10} stories` : ''}
` : ''}

${timeline && timeline.milestones?.length > 0 ? `
⏱️ MARCOS DO PROJETO:
${timeline.milestones.slice(0, 5).map((m: any) => `- ${m.title}: ${m.description}`).join('\n')}
` : ''}

💾 MODELOS DE DADOS DETALHADOS:
${dataModels.map(model => `
📋 ${model.name} (${model.tableName || model.name.toLowerCase()})
   Descrição: ${model.description || 'N/A'}
   Campos: ${model.fields?.map((f: any) => `${f.name}:${f.type}${f.validation?.required ? '*' : ''}`).join(', ') || 'N/A'}
   Relacionamentos: ${model.relationships?.length || 0} relacionamentos`
).join('\n')}

⚙️ CONFIGURAÇÕES DA API:
- Estilo: ${apiStyle}
- Autenticação: ${authRequired ? 'JWT/Token obrigatório' : 'Pública'}
- Base URL: /api/v1

🎯 INSTRUÇÕES PARA GERAÇÃO DE ENDPOINTS:

1. **ENDPOINTS CRUD COMPLETOS**: Para cada modelo, crie operações completas:
   - GET /api/{resource} (listar com paginação, filtros, busca)
   - GET /api/{resource}/{id} (obter por ID)
   - POST /api/{resource} (criar)
   - PUT /api/{resource}/{id} (atualizar completo)
   - PATCH /api/{resource}/{id} (atualizar parcial)
   - DELETE /api/{resource}/{id} (excluir)

2. **ENDPOINTS ESPECÍFICOS DO NEGÓCIO**: Baseado nas user stories e objetivos:
   - Ações específicas mencionadas nas stories
   - Operações de negócio únicas
   - Integrações necessárias
   - Relatórios e dashboards

3. **RECURSOS AVANÇADOS**:
   - Paginação (page, limit, offset)
   - Filtros dinâmicos baseados nos campos
   - Busca textual onde apropriado
   - Ordenação por campos relevantes
   - Validações específicas do domínio

4. **CÓDIGOS DE STATUS APROPRIADOS**:
   - 200: Success, 201: Created, 204: No Content
   - 400: Bad Request, 401: Unauthorized, 403: Forbidden
   - 404: Not Found, 422: Validation Error, 500: Internal Error

FORMATO DE RESPOSTA - RETORNE APENAS JSON (sem markdown):
[
  {
    "id": "endpoint-uuid",
    "path": "/api/v1/resource",
    "method": "GET|POST|PUT|PATCH|DELETE",
    "summary": "Resumo claro da operação",
    "description": "Descrição detalhada incluindo contexto de negócio",
    "parameters": [
      {
        "name": "parameter_name",
        "type": "query|path|header|body",
        "dataType": "string|integer|boolean|object|array",
        "required": true,
        "description": "Descrição do parâmetro",
        "example": "exemplo_valor"
      }
    ],
    "responses": [
      {
        "statusCode": 200,
        "description": "Descrição da resposta",
        "schema": "Nome do schema de resposta",
        "example": {"data": "exemplo_estruturado"}
      }
    ],
    "tags": ["Categoria funcional"],
    "requiresAuth": ${authRequired},
    "relatedStories": ["story-id-relacionada"]
  }
]

GERE ENDPOINTS ABUNDANTES E ESPECÍFICOS - prefira mais endpoints úteis do que poucos genéricos!`;
  }

  static buildResourcesPrompt(request: any): string {
    return `Você é um especialista em gestão de projetos de software e estimativa de recursos. Analise o projeto e sugira uma equipe ideal com estimativas precisas.

INFORMAÇÕES DO PROJETO:
Descrição: ${request.projectDescription}
Objetivos: ${request.objectives?.join(', ') || 'Não definidos'}
Solução Técnica: ${request.technicalSolution?.description || 'Não definida'}
Timeline: ${request.timeline ? `${request.timeline.totalDuration} dias` : 'Não definido'}
Stories: ${request.stories ? `${request.stories.stories?.length || 0} user stories` : 'Não definidas'}
Schema: ${request.schema ? `${request.schema.dataModels?.length || 0} modelos, ${request.schema.apiEndpoints?.length || 0} endpoints` : 'Não definido'}

PREFERÊNCIAS:
- Incluir PM: ${request.includeProjectManager ? 'Sim' : 'Não'}
- Incluir Designer: ${request.includeDesigner ? 'Sim' : 'Não'}
- Incluir DevOps: ${request.includeDevOps ? 'Sim' : 'Não'}  
- Incluir QA: ${request.includeQA ? 'Sim' : 'Não'}

ANALISE E RETORNE APENAS JSON:
{
  "resources": [
    {
      "id": "resource_xxx",
      "name": "string (ex: Desenvolvedor Full-stack Sênior)",
      "type": "developer|designer|pm|qa|devops",
      "hoursEstimated": number,
      "hourlyRate": number,
      "totalCost": number
    }
  ],
  "estimatedPrice": number,
  "suggestions": {
    "team_composition": "Explicação da composição da equipe",
    "cost_breakdown": "Breakdown dos custos principais",
    "timeline_considerations": "Considerações sobre prazos"
  }
}

CONSIDERE:
- Complexidade técnica baseada nos dados fornecidos
- Horas realistas para cada função (developer: 40-200h, designer: 20-80h, pm: 15-40h, qa: 20-60h, devops: 10-40h)
- Taxas de mercado brasileiro (dev: R$56-144/h, designer: R$42-108/h, pm: R$63-162/h, qa: R$35-90/h, devops: R$60-153/h)
- Senioridade adequada para o projeto
- Distribuição de horas por fase do projeto`;
  }

  static buildCostEstimationPrompt(request: any): string {
    return `Você é um especialista em estimativa de custos de projetos de software. Faça uma análise detalhada dos custos.

PROJETO: ${request.projectDescription}
COMPLEXIDADE: ${request.complexity || 'medium'}
TIMELINE: ${request.timeline ? JSON.stringify(request.timeline) : 'Não definido'}
FUNÇÕES DESEJADAS: ${request.includeRoles.join(', ')}

Retorne APENAS JSON:
{
  "estimatedHours": number,
  "estimatedCost": number,
  "breakdown": {
    "developer": {"hours": number, "cost": number},
    "designer": {"hours": number, "cost": number},
    "pm": {"hours": number, "cost": number},
    "qa": {"hours": number, "cost": number},
    "devops": {"hours": number, "cost": number}
  }
}`;
  }

  static buildTeamOptimizationPrompt(request: any): string {
    return `Você é um consultor especializado em otimização de equipes de desenvolvimento. Analise e otimize a composição da equipe.

PROJETO: ${request.projectDescription}
EQUIPE ATUAL: ${JSON.stringify(request.currentResources || [])}
RESTRIÇÕES: ${JSON.stringify(request.constraints || {})}

Retorne APENAS JSON:
{
  "optimizedTeam": [
    {
      "id": "resource_xxx", 
      "name": "string",
      "type": "developer|designer|pm|qa|devops",
      "hoursEstimated": number,
      "hourlyRate": number,
      "totalCost": number
    }
  ],
  "reasoning": "Explicação das otimizações feitas",
  "costSavings": number
}`;
  }
}